import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import OnboardingNav from '../components/onboarding/OnboardingNav';
import PrimaryButton from '../components/onboarding/PrimaryButton';
import { useGlobalState } from '../components/AppContext';
import { normalizeEmail } from '../utils/onboardingValidation';
import { getCallableFriendlyErrorMessage } from '../utils/callableErrors';
import { getPinnedOrDirectCallable } from '../utils/pinnedCallables';
import { trackOpenAiOnboardingStep } from '../utils/trackOpenAiOnboardingStep';
import '../assets/css/Onboarding.css';

const CODE_LENGTH = 6;

function EnvelopeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5L4 8V6l8 5 8-5v2z" />
    </svg>
  );
}

function OnboardingEmailVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const [globalEmail] = useGlobalState('email');
  const email = normalizeEmail(
    location.state?.email || globalEmail || ''
  );

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [showVerifyTooltip, setShowVerifyTooltip] = useState(false);
  const [debugVerificationCode, setDebugVerificationCode] = useState(
    location.state?.debugVerificationCode || ''
  );
  const inputsRef = useRef([]);

  const verifyOnboardingEmail = getPinnedOrDirectCallable(
    'verifyOnboardingEmail'
  );
  const resendOnboardingVerification = getPinnedOrDirectCallable(
    'resendOnboardingVerification'
  );

  useEffect(() => {
    if (!email) {
      navigate('/signup', { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    const previous = document.body.style.cssText;
    document.body.style.cssText =
      'margin:0;padding:0;min-height:100vh;display:block;overflow-x:hidden;background:#f5f7fa;background-image:none;';
    return () => {
      document.body.style.cssText = previous;
    };
  }, []);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const code = useMemo(() => digits.join(''), [digits]);
  const canVerify = code.length === CODE_LENGTH && !loading;
  const verifyDisabledReason =
    loading ? 'Verifying your email…' : 'Enter the 6-digit code from your email';

  const updateDigit = (index, value) => {
    const cleaned = value.replace(/\D/g, '');
    if (!cleaned) {
      setDigits((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }

    const chars = cleaned.split('');
    setDigits((prev) => {
      const next = [...prev];
      let cursor = index;
      chars.forEach((char) => {
        if (cursor < CODE_LENGTH) {
          next[cursor] = char;
          cursor += 1;
        }
      });
      setActiveIndex(Math.min(cursor, CODE_LENGTH - 1));
      return next;
    });
    setError('');
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      setActiveIndex(index - 1);
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!canVerify) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await verifyOnboardingEmail({ email, code });
      if (result?.data?.code !== 200) {
        throw new Error(result?.data?.message || 'Verification failed');
      }
      trackOpenAiOnboardingStep(2, 'completed');
      navigate('/workspace-setup', { state: { email } });
    } catch (err) {
      console.error('Email verification error:', err);
      setError(
        getCallableFriendlyErrorMessage(
          err,
          'That verification code is invalid or expired. Please try again.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendBusy(true);
    setResendMessage('');
    setError('');
    try {
      const result = await resendOnboardingVerification({ email });
      if (result?.data?.alreadyVerified) {
        trackOpenAiOnboardingStep(2, 'completed');
        navigate('/workspace-setup', { state: { email } });
        return;
      }
      const nextDebugCode = result?.data?.debugVerificationCode;
      if (nextDebugCode) {
        setDebugVerificationCode(nextDebugCode);
        setResendMessage('Local email is skipped. Use the code below.');
      } else {
        setResendMessage('A new verification code is on its way.');
      }
      setDigits(Array(CODE_LENGTH).fill(''));
      setActiveIndex(0);
      inputsRef.current[0]?.focus();
    } catch (err) {
      console.error('Resend verification error:', err);
      setError(
        getCallableFriendlyErrorMessage(
          err,
          'We could not resend the email right now. Please try again.'
        )
      );
    } finally {
      setResendBusy(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <div className="onboarding-verify-page">
      <OnboardingNav />
      <main className="onboarding-verify-stage">
        <form className="onboarding-verify-card" onSubmit={handleVerify}>
          <div className="onboarding-verify-icon">
            <EnvelopeIcon />
          </div>
          <h1 className="onboarding-verify-title">Check your inbox</h1>
          <p className="onboarding-verify-copy">
            {debugVerificationCode
              ? 'Local signup skips SendGrid. Use this code for'
              : 'We sent a verification code to'}
            <strong>{email}</strong>
          </p>
          {debugVerificationCode ? (
            <p className="onboarding-local-code" role="status">
              {debugVerificationCode}
            </p>
          ) : null}

          <div className="onboarding-code-row" role="group" aria-label="Verification code">
            {digits.map((digit, index) => (
              <input
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                ref={(el) => {
                  inputsRef.current[index] = el;
                }}
                className={`onboarding-code-box ${
                  index === activeIndex ? 'is-active' : ''
                } ${error ? 'is-error' : ''}`.trim()}
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={index === 0 ? CODE_LENGTH : 1}
                placeholder="–"
                aria-label={`Digit ${index + 1}`}
                value={digit}
                onFocus={() => setActiveIndex(index)}
                onChange={(e) => {
                  updateDigit(index, e.target.value);
                  const nextIndex = Math.min(
                    index + e.target.value.replace(/\D/g, '').length,
                    CODE_LENGTH - 1
                  );
                  inputsRef.current[nextIndex]?.focus();
                }}
                onKeyDown={(e) => handleKeyDown(index, e)}
              />
            ))}
          </div>

          {error ? (
            <p className="onboarding-error" role="alert">
              {error}
            </p>
          ) : null}
          {resendMessage ? (
            <p className="onboarding-helper" role="status">
              {resendMessage}
            </p>
          ) : null}

          <PrimaryButton
            type="submit"
            disabled={!canVerify}
            disabledTooltip={canVerify ? undefined : verifyDisabledReason}
            showDisabledTooltip={showVerifyTooltip && !canVerify}
            onShowDisabledTooltip={() => setShowVerifyTooltip(true)}
            loading={loading}
            showArrow={false}
          >
            {loading ? 'Verifying…' : 'Verify Email'}
          </PrimaryButton>

          <div className="onboarding-resend">
            <p className="onboarding-resend-line">
              Didn&apos;t receive it?{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={resendBusy}
                title={resendBusy ? 'Sending a new verification email…' : undefined}
              >
                {resendBusy ? 'Sending…' : 'Resend email'}
              </button>
            </p>
            <p className="onboarding-resend-hint">
              Check spam folder or use a different email address
            </p>
          </div>

          <p className="onboarding-security-note">
            Email verification prevents fake accounts and enables password
            recovery.
          </p>
        </form>
      </main>
    </div>
  );
}

export default OnboardingEmailVerification;
